import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { Send, CheckCircle, ArrowRight, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { trackPhoneCall } from "@/lib/analytics";

const contactSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50),
  lastName: z.string().trim().min(1, "Last name is required").max(50),
  email: z.string().trim().email("Please enter a valid email address").max(255),
  company: z.string().trim().min(1, "Company is required").max(200),
  telephone: z.string().trim().max(30).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

type ContactFormValues = z.infer<typeof contactSchema>;

const inputClasses =
  "h-12 bg-white border-0 text-background rounded-lg text-base shadow-sm placeholder:text-background/40 focus-visible:ring-2 focus-visible:ring-background/20 focus-visible:ring-offset-0";

const textareaClasses =
  "bg-white border-0 text-background rounded-lg text-base shadow-sm placeholder:text-background/40 focus-visible:ring-2 focus-visible:ring-background/20 focus-visible:ring-offset-0 resize-none min-h-[140px]";

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      telephone: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-lead", {
        body: {
          name: `${data.firstName} ${data.lastName}`,
          email: data.email,
          company: data.company,
          phone: data.telephone || "Not provided",
          project: data.message,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast.success("Message sent successfully!");
      setTimeout(() => navigate("/thank-you"), 1500);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">
        {/* Left column — messaging */}
        <div className="lg:col-span-2 text-center lg:text-left lg:sticky lg:top-32">
          <p className="font-serif text-display-sm md:text-display-md lg:text-[2.5rem] lg:leading-[1.15] text-primary-foreground/90 mb-6">
            Ready to build marketing that actually works?
          </p>
          <h2 className="font-serif text-heading-lg md:text-display-sm text-primary-foreground mb-4">
            Let's start a conversation
          </h2>
          <p className="text-body-lg text-primary-foreground/80 mb-6">
            Tell us about your business and what you're looking to achieve. We respond immediately.
          </p>
          <div className="hidden lg:flex flex-col gap-3 pt-4 border-t border-primary-foreground/20">
            <p className="text-body-md text-primary-foreground/60">
              Trusted by 100+ brands since 2013
            </p>
            <a
              href="tel:+442034883090"
              onClick={() => trackPhoneCall("contact_section")}
              className="inline-flex items-center gap-2 text-body-md font-medium text-primary-foreground/90 hover:text-white transition-colors"
              aria-label="Call us on +44 (0) 203 488 3090"
            >
              <Phone size={18} />
              Prefer to call? +44 (0) 203 488 3090
            </a>
          </div>
        </div>

        {/* Right column — form card */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl p-10 md:p-12 text-center shadow-lg"
              >
                <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="font-serif text-heading-lg text-background mb-2">Thank you</h3>
                <p className="text-body-md text-background/70">We'll be in touch shortly.</p>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-white/15 shadow-lg">
                  {/* Trust badges */}
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-6 pb-6 border-b border-white/10">
                    <span className="text-xs text-white/60 font-medium uppercase tracking-wider">Trusted by 100+ brands since 2013</span>
                    <span className="hidden sm:inline text-white/20">|</span>
                    <span className="text-xs text-white/50">We respond immediately</span>
                  </div>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <FormField
                          control={form.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white font-medium text-base">First name</FormLabel>
                              <FormControl>
                                <Input placeholder="Jane" {...field} className={inputClasses} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white font-medium text-base">Last name</FormLabel>
                              <FormControl>
                                <Input placeholder="Smith" {...field} className={inputClasses} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-medium text-base">Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="jane@company.com" {...field} className={inputClasses} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white font-medium text-base">Company</FormLabel>
                              <FormControl>
                                <Input placeholder="Acme Inc" {...field} className={inputClasses} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="telephone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-white font-medium text-base">
                                Telephone <span className="text-white/50 font-normal">(optional)</span>
                              </FormLabel>
                              <FormControl>
                                <Input type="tel" placeholder="+44 7700 900000" {...field} className={inputClasses} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white font-medium text-base">Message</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Tell us about your business, what you're looking to achieve, and any timelines you have in mind."
                                rows={5}
                                {...field}
                                className={textareaClasses}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full bg-background text-foreground hover:bg-background/85 active:bg-background/80 rounded-lg h-12 text-base font-semibold shadow-none hover:translate-y-[-1px] active:translate-y-[1px] transition-all duration-200 mt-2"
                      >
                        {isSubmitting ? "Sending…" : "Send message"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  </Form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
